import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { PlanBadge } from './PlanBadge';
import { useUpgradeFlow } from '@/hooks/useUpgradeFlow';
import { ArrowRight, Check } from 'lucide-react';
import type { Plan } from '@deepguide-ai/types';
import { useEffect, useRef } from 'react';
import {
  trackUpgradePromptShown,
  trackUpgradePromptDismissed,
} from '@/utils/analytics/planRestrictionEvents';

export interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  // Feature context
  featureName?: string;
  featureDescription?: string;
  featureIcon?: React.ReactNode;
  featureBenefits?: string[];

  // Plan context
  currentPlan: Plan;
  requiredPlan: Plan;

  // Optional customization
  customMessage?: string;
  showComparison?: boolean;

  // Analytics
  source?: string; // Track where upgrade was triggered
}

/**
 * Unified modal for all upgrade flows
 *
 * @example
 * ```tsx
 * <UpgradeDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   featureName="Advanced Analytics"
 *   featureDescription="Get detailed insights into your documentation usage"
 *   featureBenefits={[
 *     'Real-time usage analytics',
 *     'Custom date ranges',
 *     'Export reports as CSV'
 *   ]}
 *   currentPlan="FREE"
 *   requiredPlan="PRO"
 *   source="analytics_page"
 * />
 * ```
 */
export function UpgradeDialog({
  open,
  onOpenChange,
  featureName,
  featureDescription,
  featureIcon,
  featureBenefits = [],
  currentPlan,
  requiredPlan,
  customMessage,
  source,
}: UpgradeDialogProps) {
  const { startUpgrade, isUpgrading } = useUpgradeFlow();
  const shownTimeRef = useRef<number | null>(null);
  const hasTrackedShown = useRef(false);

  const formatPlanName = (plan: Plan) =>
    plan.charAt(0) + plan.slice(1).toLowerCase();

  // Track when dialog is shown
  useEffect(() => {
    if (open && !hasTrackedShown.current) {
      shownTimeRef.current = Date.now();
      trackUpgradePromptShown({
        feature: featureName || 'unknown',
        source: source || 'unknown',
        currentPlan,
        requiredPlan,
      });
      hasTrackedShown.current = true;
    }

    // Reset tracking flag when dialog closes
    if (!open) {
      hasTrackedShown.current = false;
    }
  }, [open, featureName, source, currentPlan, requiredPlan]);

  const handleUpgrade = () => {
    startUpgrade({
      targetPlan: requiredPlan as 'STARTER' | 'PRO' | 'ENTERPRISE',
      source,
      feature: featureName,
    });
  };

  const handleViewBilling = () => {
    window.open('https://screenata.com/pricing', '_blank', 'noopener,noreferrer');
  };

  const handleDismiss = () => {
    // Track dismissal with time on screen
    const timeOnScreen = shownTimeRef.current
      ? Date.now() - shownTimeRef.current
      : undefined;

    trackUpgradePromptDismissed({
      feature: featureName || 'unknown',
      source: source || 'unknown',
      requiredPlan,
      currentPlan,
      timeOnScreen,
    });

    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
          return;
        }
        handleDismiss();
      }}
    >
      <DialogContent className="overflow-hidden sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{featureName || 'Upgrade your plan'}</DialogTitle>
          {featureDescription && (
            <DialogDescription>
              {featureDescription}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 px-6 py-4">
          {featureIcon && (
            <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-surface-subtle text-cobalt-strong">
              {featureIcon}
            </div>
          )}

          <div className="flex w-full max-w-full items-center justify-between gap-4 rounded-sm border border-border bg-surface-subtle px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[1px] text-text-subtle">
                Current
              </div>
              <div className="mt-1">
                <PlanBadge plan={currentPlan} variant="compact" />
              </div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-text-placeholder" />
            <div className="min-w-0 text-right">
              <div className="text-[10px] font-semibold uppercase tracking-[1px] text-text-subtle">
                Required
              </div>
              <div className="mt-1 flex justify-end">
                <PlanBadge plan={requiredPlan} variant="compact" />
              </div>
            </div>
          </div>

          {featureBenefits.length > 0 && (
            <div className="space-y-2">
              {featureBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-cobalt-strong" />
                  <span className="text-[13px] leading-5 text-text-body">
                    {benefit}
                  </span>
                </div>
              ))}
            </div>
          )}

          {customMessage && (
            <p className="text-[13px] leading-5 text-text-subtle">
              {customMessage}
            </p>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button
            variant="ghost"
            onClick={handleViewBilling}
            className="h-9 px-0 text-[13px] text-muted-foreground hover:bg-transparent hover:text-cobalt-strong sm:mr-auto"
          >
            Learn more about plans
          </Button>
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="h-9 text-[13px]"
          >
            Maybe later
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={isUpgrading}
            className="h-9 bg-primary text-[13px] text-primary-foreground hover:bg-primary/90"
          >
            {isUpgrading ? (
              <>Redirecting...</>
            ) : (
              <>
                Upgrade to {formatPlanName(requiredPlan)}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
